import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { DataGrid, GridColumnMenu, GridToolbar } from "@mui/x-data-grid";
import Loader from "../../components/common/Loader";
import Badge from "@mui/material/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import SecCard from "../../components/common/SecCard";
import { Row, Col } from "react-bootstrap";

import { green, yellow, red, deepPurple, blue } from "@mui/material/colors";
import { SERVER_ORIGIN } from "../../utilities/constants";
import {
  hasReadOnlyAccess,
  getDashboardPath,
} from "../../utilities/helper_functions";
import HeaderCard from "../../components/common/HeaderCard";
import BackButton from "../../components/common/BackButton";

import css from "../../css/admin/users-page.module.css";

const AllTermsAndConditions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [allTerms, setAllTerms] = useState([]);
  const [page, setPage] = useState(1);
  const [sortType, setSortType] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [financialYearFilter, setFinancialYearFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [financialYears, setFinancialYears] = useState([]);

  // Dialog states
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openActivateDialog, setOpenActivateDialog] = useState(false);
  const [openArchiveDialog, setOpenArchiveDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const navigate = useNavigate();
  const isReadOnly = hasReadOnlyAccess();

  useEffect(() => {
    getAllTermsAndConditions();
    getFinancialYears();
  }, [page, sortType, searchQuery, financialYearFilter, statusFilter]);

  const getAllTermsAndConditions = async () => {
    setIsLoading(true);

    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        search: searchQuery,
        sortBy: "createdAt",
        sortType: sortType,
      });

      if (financialYearFilter) {
        queryParams.append("financialYear", financialYearFilter);
      }
      if (statusFilter) {
        queryParams.append("status", statusFilter);
      }

      const response = await fetch(
        `${SERVER_ORIGIN}/api/terms/auth/all?${queryParams}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "auth-token": sessionStorage.getItem("token"),
          },
        }
      );

      const result = await response.json();
      setIsLoading(false);

      if (response.status >= 400 && response.status < 600) {
        if (response.status === 401) {
          navigate("/admin/login");
        } else {
          toast.error(result.statusText);
        }
      } else if (response.status === 200) {
        setAllTerms(result.data || []);
        setTotalPages(result.pagination?.totalPages || 0);
      }
    } catch (err) {
      setIsLoading(false);
      toast.error("Error fetching Terms and Conditions");
    }
  };

  const getFinancialYears = async () => {
    try {
      const response = await fetch(
        `${SERVER_ORIGIN}/api/terms/auth/financial-years/list`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "auth-token": sessionStorage.getItem("token"),
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setFinancialYears(result.data || []);
      }
    } catch (err) {
      console.error("Error fetching financial years:", err);
    }
  };

  // Status rendering function
  const renderStatus = (params) => {
    let statusColor;
    let statusText;
    let textColor = "white";

    switch (params.value) {
      case "active":
        statusColor = green[500];
        statusText = "Active";
        break;
      case "draft":
        statusColor = yellow[600];
        statusText = "Draft";
        textColor = "black";
        break;
      case "archived":
        statusColor = red[500];
        statusText = "Archived";
        break;
      default:
        statusColor = "black";
        statusText = "Unknown";
    }

    return (
      <Badge>
        <span
          style={{
            backgroundColor: statusColor,
            padding: "6.4px 16px",
            borderRadius: "8px",
            color: textColor,
          }}
        >
          {statusText}
        </span>
      </Badge>
    );
  };

  // Dialog handlers
  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setOpenDeleteDialog(true);
  };

  const handleActivateClick = (id) => {
    setSelectedId(id);
    setOpenActivateDialog(true);
  };

  const handleArchiveClick = (id) => {
    setSelectedId(id);
    setOpenArchiveDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
    setSelectedId(null);
  };

  const handleActivateDialogClose = () => {
    setOpenActivateDialog(false);
    setSelectedId(null);
  };

  const handleArchiveDialogClose = () => {
    setOpenArchiveDialog(false);
    setSelectedId(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(
        `${SERVER_ORIGIN}/api/terms/auth/dlt/${selectedId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "auth-token": sessionStorage.getItem("token"),
          },
        }
      );

      const result = await response.json();
      if (response.status === 200) {
        toast.success("Terms and Conditions deleted successfully");
        getAllTermsAndConditions();
      } else {
        toast.error(
          result.statusText || "Failed to delete Terms and Conditions"
        );
      }
    } catch (err) {
      toast.error("Error deleting Terms and Conditions");
    }
    handleDeleteDialogClose();
  };

  const handleActivateConfirm = async () => {
    try {
      const response = await fetch(
        `${SERVER_ORIGIN}/api/terms/auth/activate/${selectedId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "auth-token": sessionStorage.getItem("token"),
          },
        }
      );

      const result = await response.json();
      if (response.status === 200) {
        toast.success("Terms and Conditions activated successfully");
        getAllTermsAndConditions();
      } else {
        toast.error(
          result.statusText || "Failed to activate Terms and Conditions"
        );
      }
    } catch (err) {
      toast.error("Error activating Terms and Conditions");
    }
    handleActivateDialogClose();
  };

  const handleArchiveConfirm = async () => {
    try {
      const response = await fetch(
        `${SERVER_ORIGIN}/api/terms/auth/archive/${selectedId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "auth-token": sessionStorage.getItem("token"),
          },
        }
      );

      const result = await response.json();
      if (response.status === 200) {
        toast.success("Terms and Conditions archived successfully");
        getAllTermsAndConditions();
      } else {
        toast.error(
          result.statusText || "Failed to archive Terms and Conditions"
        );
      }
    } catch (err) {
      toast.error("Error archiving Terms and Conditions");
    }
    handleArchiveDialogClose();
  };

  const rows = allTerms.map((term, idx) => ({
    ...term,
    id: idx + 1,
    emName: term.emData?.emName || "N/A",
    chapterName: term.emData?.chapterName || "N/A",
    regionName: term.emData?.regionName || "N/A",
  }));

  const columns = [
    {
      field: "id",
      headerName: "S.No",
      width: 70,
    },
    {
      field: "title",
      headerName: "Title",
      width: 250,
    },
    // {
    //     field: 'version',
    //     headerName: 'Version',
    //     width: 100,
    // },
    {
      field: "financialYear",
      headerName: "Financial Year",
      width: 250,
    },
    {
      field: "termsfor",
      headerName: "Terms For",
      width: 250,
    },
    // {
    //   field: "chapterName",
    //   headerName: "Chapter",
    //   width: 150,
    // },
    // {
    //   field: "regionName",
    //   headerName: "Region",
    //   width: 120,
    // },
    // {
    //   field: "emName",
    //   headerName: "EM Name",
    //   width: 150,
    // },
    {
      field: "status",
      headerName: "Status",
      width: 250,
      renderCell: renderStatus,
    },
    // {
    //     field: 'effectiveDate',
    //     headerName: 'Effective Date',
    //     width: 120,
    //     renderCell: (params) => {
    //         return new Date(params.value).toLocaleDateString();
    //     }
    // },
    {
      headerName: "Actions",
      width: 300,
      renderCell: (params) => {
        if (isReadOnly) {
          return (
            <div className="d-flex justify-content-center">
              <span className="badge bg-secondary">View Only</span>
            </div>
          );
        }

        const status = params.row.status;

        return (
          <div className="d-flex justify-content-between gap-1">
            {/* <button
                            className="btn btn-sm btn-primary"
                            onClick={() => navigate(`/terms-conditions/edit/${params.row._id}`)}
                        >
                            Edit
                        </button> */}
            {(status === "draft" || status === "archived") && (
              <button
                className="btn btn-sm btn-success"
                onClick={() => handleActivateClick(params.row._id)}
              >
                {status === "archived" ? "Re-Activate" : "Activate"}
              </button>
            )}

            {status === "active" && (
              <button
                className="btn btn-sm btn-warning"
                onClick={() => handleArchiveClick(params.row._id)}
              >
                Archive
              </button>
            )}
            <button
              className="btn btn-sm btn-danger"
              onClick={() => handleDeleteClick(params.row._id)}
            >
              Delete
            </button>
          </div>
        );
      },
    },
  ];

  function handleAddTermsClick() {
    navigate("/terms-conditions/add");
  }

  function CustomColumnMenu(props) {
    return <GridColumnMenu {...props} slots={{}} slotProps={{}} />;
  }

  return (
    <div className={css.outerDiv}>
      {/* <HeaderCard>
                <BackButton text="Back to Dashboard" customPath={getDashboardPath()} />
                <h1 className="headerTitle">Terms and Conditions</h1>
                <hr />
                {isReadOnly ? (
                    <div>
                        <p className="headerSubtitle">
                            You have read-only access to view Terms and Conditions data.
                        </p>
                        <div className="alert alert-info" role="alert">
                            <strong>Note:</strong> As a Region EM user, you can view all data but cannot add, edit, or delete any records.
                        </div>
                    </div>
                ) : (
                    <div>
                        <p className="headerSubtitle">
                            You can view, add, edit, or delete Terms and Conditions from here
                        </p>
                        <p className="headerSubtitle">
                            Note: Deleting Terms and Conditions is irreversible. Do it at your own risk.
                        </p>
                        <div className='d-flex justify-content-center align-items-baseline mt-3'>
                            <button
                                className={`${css.fileUploadBtn} commonBtn`}
                                onClick={handleAddTermsClick}
                            >
                                <FontAwesomeIcon icon={faPlus} style={{ color: "#ffffff", marginRight: "5px" }} /> 
                                Add Terms and Conditions
                            </button>
                        </div>
                    </div>
                )}
            </HeaderCard> */}
      <div className={css.headerSection}>
        <div className={css.headerLeft}>
          <BackButton
            text="Back to Dashboard"
            customPath={getDashboardPath()}
          />
        </div>
      </div>
      {hasReadOnlyAccess() && (
        <div className={css.readOnlyAlert}>
          <div className="alert alert-info" role="alert">
            <strong>Read-Only Access:</strong> You can view all Terms and conditions data
            but cannot add, edit, or delete any records.
          </div>
        </div>
      )}
      <div className="my-4">
        <SecCard>
          <Row className="g-4">
            <div
              className={`${css.serviceHeader} d-flex justify-content-between align-items-center mb-3`}
            >
              <div className={css.serviceName}>Terms and Conditions</div>

              <div className={css.headerRight}>
                {!hasReadOnlyAccess() && (
                  <button className={css.addBtn} onClick={handleAddTermsClick}>
                    <FontAwesomeIcon icon={faPlus} className={css.addIcon} />
                    Add Terms and Conditions{" "}
                  </button>
                )}
              </div>
            </div>
          </Row>
        </SecCard>
      </div>

      <div className={css.filterBtns}>
        <div
          className="d-flex gap-3"
          style={{
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {/* Filters */}
          <div className="d-flex gap-2">
            <select
              value={financialYearFilter}
              onChange={(e) => setFinancialYearFilter(e.target.value)}
              className="form-select form-select-sm"
              style={{ width: "150px" }}
            >
              <option value="">All Financial Years</option>
              {financialYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-select form-select-sm"
              style={{ width: "120px" }}
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Search */}
          <div
            className="d-flex"
            style={{ position: "relative", alignItems: "center" }}
          >
            <input
              type="text"
              value={searchQuery}
              placeholder="Search Terms and Conditions"
              onChange={(e) => setSearchQuery(e.target.value)}
              className={css.searchInput}
            />
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              style={{ color: "black", position: "absolute", right: "10px" }}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <div>
          <DataGrid
            rows={rows}
            columns={columns}
            // initialState={{
            //     pagination: {
            //         paginationModel: { pageSize: 10, page: 0 },
            //     },
            // }}
            autoHeight
            density="compact"
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            sx={{
              borderRadius: 2,
              border: "1px solid #e5e7eb",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f9fafb",
                color: "#111827",
                fontWeight: 600,
              },
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#fcfcfd",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#eef2ff",
              },
            }}
            slots={{ columnMenu: CustomColumnMenu, toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 300 },
              },
            }}
            loading={isLoading}
          />
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete these Terms and Conditions? This
            action is irreversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation Dialog */}
      <Dialog
        open={openActivateDialog}
        onClose={handleActivateDialogClose}
        aria-labelledby="activate-dialog-title"
        aria-describedby="activate-dialog-description"
      >
        <DialogTitle id="activate-dialog-title">Confirm Activation</DialogTitle>
        <DialogContent>
          <DialogContentText id="activate-dialog-description">
            Are you sure you want to activate these Terms and Conditions? They
            will become live and visible to users.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleActivateDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleActivateConfirm} color="success" autoFocus>
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={openArchiveDialog}
        onClose={handleArchiveDialogClose}
        aria-labelledby="archive-dialog-title"
        aria-describedby="archive-dialog-description"
      >
        <DialogTitle id="archive-dialog-title">Confirm Archive</DialogTitle>
        <DialogContent>
          <DialogContentText id="archive-dialog-description">
            Are you sure you want to archive these Terms and Conditions? They
            will no longer be active but can be reactivated later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchiveDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleArchiveConfirm} color="warning" autoFocus>
            Archive
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AllTermsAndConditions;
